// THE GLYPH NAME (dropdowns spec 2026-09-30 §3.3, §12) — one answer for every surface that shows an app glyph:
// the search launcher (the PIXEL set, `px`) and the navigation (the LINE set, `ln`: the navbar tabs and the home
// App strip in both looks). A tile names its glyph (`glyph`, a key of scripts/gen-glyph-icons.mjs: a built-in
// row, or a module app in its OWN manifest); a route the redesign drew answers next; anything else wears the
// line-set fallback (`apps`, the installed-app glyph). Manifest data is never trusted as a name: only a bare key is read, and only
// when the element's design system carries that glyph. The route map lives INSIDE the function (frank: no
// module-scope capture). Called as el.call('glyphNameFor', { glyph, route, icon }, 'ln' | 'px').
export function glyphNameFor(tile, set) {
  const t = tile || {}
  const prefix = set === 'ln' ? 'ln' : 'px'
  const icons = this && this.context && this.context.designSystem && this.context.designSystem.icons
  const nameOf = (key) => prefix + key[0].toUpperCase() + key.slice(1)
  const usable = (key) =>
    typeof key === 'string' && /^[a-z][A-Za-z0-9]*$/.test(key) && (!icons || !!icons[nameOf(key)])
  if (usable(t.glyph)) return nameOf(t.glyph)
  const byRoute = {
    '/': 'home',
    '/studio': 'studio',
    '/infra': 'infra',
    '/terminal': 'terminal',
    '/tasks': 'tasks',
    '/notes': 'notes',
    '/files': 'files',
    '/chat': 'chat',
    '/mail': 'mail',
    '/calendar': 'calendar',
    '/meet': 'meet',
    '/reminders': 'reminders',
    '/bookmarks': 'bookmarks',
    '/history': 'history',
    '/announcements': 'broadcast',
    '/team-and-agents': 'teamAndAgents',
    '/agents': 'agents',
    '/team-members': 'teamMembers',
    '/analytics': 'analytics',
    '/workspace-analytics': 'usage',
    '/system-status': 'health',
    '/crm': 'crm',
    '/realestate': 'realestate',
    '/jobs': 'jobs'
  }
  const path = String(t.route || '')
    .split('?')[0]
    .split('#')[0]
  const top = path === '/' ? '/' : '/' + (path.split('/').filter(Boolean)[0] || '')
  const routeKey = byRoute[top === '/' && path !== '/' ? '' : top]
  if (usable(routeKey)) return nameOf(routeKey)
  // A MODULE APP'S OWN WORDS (Nika 2026-10-23: "make better icons" — the icon navbar drew Payments and Risk as the
  // same app window): its route's segments, the LAST first (/keepz/risk is about risk), then the words of its name
  // and its app key, read against a fixed table of HOUSE glyphs — so the ruling below still holds: no module app
  // wears an icon of its own drawing, and no manifest text is ever taken as a name.
  const WORDS = {
    payments:
      'payment payments pay payroll billing checkout transaction transactions card cards wallet finance merchant merchants payout payouts acquiring',
    risk: 'risk risks fraud compliance security alert alerts dispute disputes chargeback chargebacks aml kyc',
    orders: 'order orders sales sale cart purchase purchases',
    inventory:
      'inventory warehouse warehouses stock products product catalog catalogue items shipments shipping logistics delivery',
    invoices: 'invoice invoices receipt receipts bill bills accounting expense expenses ledger',
    store: 'store stores shop shops storefront marketplace listing listings commerce ecommerce',
    support: 'support helpdesk service desk inbox',
    analytics: 'report reports reporting insights insight metrics stats statistics kpi kpis',
    crm: 'leads lead deals deal pipeline prospects',
    users:
      'contacts contact customers customer clients client people members member hr employees staff partners',
    calendar:
      'booking bookings appointment appointments schedule scheduling events reservation reservations',
    notes: 'docs documents document wiki knowledge pages cms content blog articles posts',
    chat: 'messages messaging conversations conversation chats',
    mail: 'email emails newsletter newsletters campaign campaigns',
    tools: 'settings setting config configuration preferences setup',
    jobs: 'careers recruiting hiring applicants vacancies',
    realestate: 'properties property units unit buildings rentals rent leases',
    health: 'status uptime incidents',
    database: 'data records tables datasets',
    lock: 'permissions access roles',
    broadcast: 'announcements news marketing promotions',
    tasks: 'todo todos projects project issues production',
    files: 'storage media assets uploads drive',
    meet: 'calls call meetings meeting video'
  }
  const words = []
  const segments = path.split('/').filter(Boolean)
  for (let i = segments.length - 1; i >= 0; i--) words.push(segments[i])
  for (const text of [t.label, t.title, t.name, t.appKey, t.key]) {
    if (typeof text === 'string' && !text.includes('.')) words.push(...text.split(/[\s_\-/]+/))
  }
  for (const raw of words) {
    const w = String(raw || '').toLowerCase()
    if (!w) continue
    for (const key of Object.keys(WORDS)) {
      if (WORDS[key].split(' ').includes(w) && usable(key)) return nameOf(key)
    }
  }
  // THE INSTALLED-APP GLYPH (coordinator ruling 2026-10-02): a tile with no valid
  // glyph — a module app whose manifest names none, or names one the line set
  // lacks — wears the ONE `apps` glyph (an app window), never its own foreign
  // icon. Only a design system that carries no glyph set at all keeps the icon.
  if (usable('apps')) return nameOf('apps')
  return t.icon || 'star'
}

// THE APP YOU ARE IN (dropdowns spec §5): the open page slot first (an app opened over the home), else the page
// itself — the app's route or a page under it. Home ('/') is never "an app you are in". The launcher tile and the
// home App strip item mark it (aria-current="page", the inverted plate, the orange dot).
export function appRouteIsCurrent(route, root) {
  const r = String(route || '').split('?')[0]
  if (!r || r === '/') return false
  const rt = root || {}
  const slot = rt._canvasSlot ? String(rt._canvasSlot).split('?')[0] : ''
  const path =
    slot ||
    String(rt.currentPath || '')
      .replace(/^\/w\/[a-z0-9-]{1,48}(?=\/|$)/, '')
      .split('?')[0]
  return path === r || path.startsWith(r + '/')
}
